import {
  AgeGroup,
  AGE_GROUPS,
  DEFAULT_STEP_MODELS,
  GenerationStep,
  JobApproval,
  jobApprovalSchema,
  JobMethod,
  jobMethodSchema,
  jobTaskCountSchema,
  Language,
  LANGUAGES,
  languageSchema,
  StepModelChoice,
  StepModelChoices,
  stepModelChoiceSchema,
} from '@quizdb/common';

/**
 * The New Job settings a started job leaves behind as the next form's starting point, kept in
 * this browser only. The request, the instructions, the study material (with its page range and
 * the photos' page language), the document and the section are never kept: they belong to one
 * job, not to the next.
 */
export interface NewJobDefaults {
  method: JobMethod;
  language: Language;
  taskCount: number;
  stepModels: StepModelChoices;
  /** The job's approval (QZ-D192). */
  approval: JobApproval;
  targetLanguages: Language[];
  ageGroups: AgeGroup[];
  /** The source sites unticked, so a site added since starts ticked and a deleted one is moot. */
  unpickedSites: string[];
}

/** The one `localStorage` key the defaults live under; per origin, so dev and live differ. */
export const NEW_JOB_DEFAULTS_KEY = 'quizdb.newJob.defaults';

/** The form's own starting point, before any job has been started in this browser. */
export const FALLBACK_NEW_JOB_DEFAULTS: NewJobDefaults = {
  method: 'prompt',
  language: 'sv',
  taskCount: 40,
  stepModels: DEFAULT_STEP_MODELS,
  approval: 'manual',
  targetLanguages: [...LANGUAGES],
  ageGroups: [...AGE_GROUPS],
  unpickedSites: [],
};

/** `undefined` where storage is unavailable, e.g. blocked by the browser, so nothing is kept. */
const browserStorage = (): Storage | undefined => {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
};

/**
 * The entries of `order` that `value` lists, in `order`'s order and without duplicates, so an
 * unknown entry drops out; `undefined` if `value` is not a list or lists none of them.
 */
const pickList = <T extends string>(value: unknown, order: readonly T[]): T[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const kept = order.filter((entry) => value.includes(entry));
  return kept.length > 0 ? kept : undefined;
};

/**
 * One step's saved choice, read on its own so a family or effort since retired costs only that
 * step; a saved `writeModel` is never read, since it is ignored, not migrated (JSM-D1).
 */
const parseStepModel = (
  savedStepModels: Record<string, unknown> | undefined,
  step: GenerationStep,
  fallback: StepModelChoices
): StepModelChoice => {
  const parsed = stepModelChoiceSchema.safeParse(savedStepModels?.[step]);
  return parsed.success ? parsed.data : fallback[step];
};

/**
 * The saved approval (QZ-D192): `saved.approval` when it parses; else the superseded
 * `saved.autoApprove`, `true` reading as `passed` and `false` as `manual`; else `fallback`.
 */
const readApproval = (saved: Record<string, unknown>, fallback: JobApproval): JobApproval => {
  const approval = jobApprovalSchema.safeParse(saved.approval);
  if (approval.success) return approval.data;
  if (saved.autoApprove === true) return 'passed';
  if (saved.autoApprove === false) return 'manual';
  return fallback;
};

/**
 * Reads the saved defaults field by field. A field that is missing or no longer valid, e.g. a
 * step's family or effort since retired, falls back on its own, and anything unreadable reads as
 * none, so a stale entry never stops the form from opening. The job language is put back among
 * the target languages, whose chip it locks (TMA-D5).
 */
export const loadNewJobDefaults = (
  storage: Storage | undefined = browserStorage()
): NewJobDefaults => {
  const fallback = FALLBACK_NEW_JOB_DEFAULTS;
  let saved: Record<string, unknown>;
  try {
    const raw = storage?.getItem(NEW_JOB_DEFAULTS_KEY) ?? null;
    if (raw === null) return { ...fallback };
    const value = JSON.parse(raw) as unknown;
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { ...fallback };
    }
    saved = value as Record<string, unknown>;
  } catch {
    return { ...fallback };
  }

  const method = jobMethodSchema.safeParse(saved.method);
  const language = languageSchema.safeParse(saved.language);
  const taskCount = jobTaskCountSchema.safeParse(saved.taskCount);
  const jobLanguage = language.success ? language.data : fallback.language;
  const targetLanguages = pickList(saved.targetLanguages, LANGUAGES) ?? fallback.targetLanguages;
  const unpickedSites = Array.isArray(saved.unpickedSites)
    ? saved.unpickedSites.filter((site): site is string => typeof site === 'string')
    : fallback.unpickedSites;
  const savedStepModels =
    typeof saved.stepModels === 'object' &&
    saved.stepModels !== null &&
    !Array.isArray(saved.stepModels)
      ? (saved.stepModels as Record<string, unknown>)
      : undefined;

  return {
    method: method.success ? method.data : fallback.method,
    language: jobLanguage,
    taskCount: taskCount.success ? taskCount.data : fallback.taskCount,
    stepModels: {
      plan: parseStepModel(savedStepModels, 'plan', fallback.stepModels),
      write: parseStepModel(savedStepModels, 'write', fallback.stepModels),
      check: parseStepModel(savedStepModels, 'check', fallback.stepModels),
    },
    approval: readApproval(saved, fallback.approval),
    targetLanguages: LANGUAGES.filter((l) => l === jobLanguage || targetLanguages.includes(l)),
    ageGroups: pickList(saved.ageGroups, AGE_GROUPS) ?? fallback.ageGroups,
    unpickedSites,
  };
};

/** Keeps `defaults` for the next New Job; a storage failure, e.g. a full quota, is ignored. */
export const saveNewJobDefaults = (
  defaults: NewJobDefaults,
  storage: Storage | undefined = browserStorage()
): void => {
  try {
    storage?.setItem(NEW_JOB_DEFAULTS_KEY, JSON.stringify(defaults));
  } catch {
    // Remembering the form is a convenience; the job has been created either way.
  }
};
